import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, concatMap, exhaustMap, tap } from 'rxjs/operators';
import { EMPTY, from, of } from 'rxjs';

import * as DataActions from './data.actions';
import { SignupService } from '../../services/signup.service';
import { CookieService } from 'ngx-cookie-service';
import { SignupOrgService } from '../../services/signup-org.service';
import { ToastService } from 'app/toast/services/toast.service';

@Injectable()
export class SignupDataEffects {

  Signup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.SignUp),
      exhaustMap(({ payload }) => {
        const { email, password, name, reCAPTCHAToken } = payload;
        return this.signupService.signup(email, password, name, reCAPTCHAToken).pipe(
          concatMap((data: any) => {
            this.cookieService.set('accessToken', data.access_token, 60, '/');
            const signupSuccessAction = DataActions.SignUpSuccess({ data });
            const loadUserAction = DataActions.LoadUser();
            return from([signupSuccessAction, loadUserAction]);
          }),
          catchError(error => of(DataActions.SignUpFailure({ error }))))
      })
    );
  });

  ChangeEMail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.ChangeEMail),
      exhaustMap(({ payload }) => {
        return this.signupService.updateEmail(payload).pipe(
          concatMap((data: any) => {
            const signupSuccessAction = DataActions.ChangeEMailSuccess({ data });
            const loadUserAction = DataActions.LoadUser();
            return from([signupSuccessAction, loadUserAction]);
          }),
          catchError(error => {
            return of(DataActions.ChangeEMailFailure({ error }))
          }))
      })
    );
  });

  ContactUs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.ContactUs),
      exhaustMap(({ payload }) => {
        return this.signupService.contactUs(payload).pipe(
          concatMap(data => {
            this.toastService.show('success', 'Message received, will get back to you soon');

            const contactUsSuccessAction = DataActions.ContactUsSuccess({ data });
            return of(contactUsSuccessAction);
          }),
          catchError(error => {
            return of(DataActions.ContactUsFailure({ error }));
          }));
      })
    );
  });

  InitiateVerifyEmail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.InitiateVerifyEmail),
      exhaustMap(() => {
        return this.signupService.initiateVerifyEmail().pipe(
          concatMap(data => {
            this.toastService.show('success', 'Email verification has been resent');
            
            const initiateVerifyEmailSuccessAction = DataActions.InitiateVerifyEmailSuccess({ data })
            return of(initiateVerifyEmailSuccessAction)
          }),
          catchError(error => of(DataActions.InitiateVerifyEmailFailure({ error }))))
      })
    );
  });

  UpdateEmail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.UpdateEmail),
      exhaustMap(({ payload }) => {
        const { email } = payload;
        return this.signupService.updateEmail(email).pipe(
          map(data => DataActions.UpdateEmailSuccess({ data })),
          catchError(error => of(DataActions.UpdateEmailFailure({ error }))))
      })
    );
  });

  LoadUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.LoadUser),
      exhaustMap(() => {
        return this.signupService.getUser().pipe(
          map(data => DataActions.LoadUserSuccess({ data })),
          catchError(error => of(DataActions.LoadUserFailure({ error }))))
      })
    );
  });

  AddInfo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.AddInfo),
      exhaustMap(({ payload }) => {
        return this.signupOrgService.addInfo(payload).pipe(
          concatMap(data => {
            const addInfoSuccessAction = DataActions.AddInfoSuccess({ data });
            const loadUserAction = DataActions.LoadUser();
            return from([addInfoSuccessAction, loadUserAction]);
          }),
          catchError(error => of(DataActions.AddInfoFailure({ error }))))
      })
    );
  });

  AddBankAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.AddBankAccount),
      exhaustMap(({ payload: { plaid_token, plaid_account_id } }) => {
        return this.signupService.addBankAccount(plaid_token, plaid_account_id).pipe(
          concatMap(data => {
            const addBankAccountAction = DataActions.AddBankAccountSuccess({ data });
            const loadUserAction = DataActions.LoadUser();
            return from([addBankAccountAction, loadUserAction]);
          }),
          catchError(error => of(DataActions.AddBankAccountFailure({ error }))))
      })
    );
  });

  AddBankAccountRoutingNumber$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.AddBankAccountRoutingNumber),
      exhaustMap(({ payload: { bankAccountToken } }) => {
        return this.signupService.addBankAccountRoutingNumber(bankAccountToken).pipe(
          concatMap(data => {
            const addBankAccountAction = DataActions.AddBankAccountRoutingNumberSuccess({ data });
            const loadUserAction = DataActions.LoadUser();
            return from([addBankAccountAction, loadUserAction]);
          }),
          catchError(error => of(DataActions.AddBankAccountRoutingNumberFailure({ error }))))
      })
    );
  });

  VerificateOrg$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.VerificateOrg),
      exhaustMap(({ payload }) => {
        return this.signupOrgService.verificate(payload).pipe(
          concatMap(data => {
            const verificateAction = DataActions.VerificateOrgSuccess({ data });
            const loadUserAction = DataActions.LoadUser();
            return from([verificateAction, loadUserAction]);
          }),
          catchError(error => of(DataActions.VerificateOrgFailure({ error }))))
      })
    );
  });

  RedeemCoupon$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.RedeemCoupon),
      exhaustMap(({ payload: code }) => {
        return this.signupOrgService.redeemCoupon(code).pipe(
          concatMap(data => {
            const redeemCouponAction = DataActions.RedeemCouponSuccess({ data });
            return from([redeemCouponAction]);
          }),
          catchError(error => of(DataActions.RedeemCouponFailure({ error }))))
      })
    );
  });

  SetPlan$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.SetPlan),
      exhaustMap(({ payload: { planType, invoiceAmount } }) => {
        return this.signupOrgService.setPlan(planType, invoiceAmount).pipe(
          concatMap(data => {
            const setPlanAction = DataActions.SetPlanSuccess({ data });
            const loadUserAction = DataActions.LoadUser();
            return from([setPlanAction, loadUserAction]);
          }),
          catchError(error => of(DataActions.SetPlanFailure({ error }))))
      })
    );
  });

  GetInvoicesDue$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.GetInvoicesDue),
      exhaustMap(() => {
        return this.signupService.getInvoicesDue().pipe(
          concatMap(data => {
            const getInvoicesDueAction = DataActions.GetInvoicesDueSuccess({ data });
            return from([getInvoicesDueAction]);
          }),
          catchError(error => of(DataActions.GetInvoicesDueFailure({ error }))))
      })
    );
  });

  constructor(
    private actions$: Actions,
    private cookieService: CookieService,
    private signupService: SignupService,
    private signupOrgService: SignupOrgService,
    private toastService: ToastService
  ) {}

}
